import { useQuery } from '@tanstack/react-query'
import { getGovernanceFinding } from '../services/governanceApi'
import type { GovernanceFinding } from '../types/governance'
import { errorKind } from '../utils/format'
import { SLOW, pollUnlessRefused } from '../utils/polling'

/**
 * Retry anything but a refusal, twice at most.
 *
 * A 403 is the caller lacking Lighthouse.Admin, and a second try cannot change
 * that — retrying one only delays the RestrictedState the reader should see. A 500
 * is retried because it may be transient; the section still names the migration
 * once the retries are spent.
 */
export function shouldRetryGovernance(failureCount: number, error: unknown): boolean {
  return errorKind(error) !== 'forbidden' && failureCount < 2
}

/**
 * One governance finding group.
 *
 * THE KEY IS SHARED, and so is this signature: the Governance Queue page, the
 * Overview preview and the rail's badge (nav/useNavBadges.ts) all read
 * `['observatory', 'governance', finding]`, so three mounts cost one request per
 * group. Do not add a parameter to the key.
 *
 * SLOW polling: ownership is flagged by a nightly sweep and grants change when a
 * person changes them in Cartographer — a governance surface, not a live one.
 *
 * `enabled` is the gate contract (hooks/useLighthouseAdmin.ts): the page passes
 * false on `denied` so it never fetches an endpoint the caller cannot read, and
 * previews and badges pass `gate.state === 'admin'`.
 */
export function useGovernanceFinding(
  finding: GovernanceFinding,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['observatory', 'governance', finding],
    queryFn: () => getGovernanceFinding(finding),
    enabled: options?.enabled ?? true,
    refetchInterval: pollUnlessRefused(SLOW),
    retry: shouldRetryGovernance,
  })
}
